import * as Monaco from 'monaco-editor';
import { parseAsString, useQueryState } from 'nuqs';

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { IngridAi16, Question16 } from '@ingrid/iconography/react';
import {
  Breadcrumb,
  Button,
  DropdownMenu,
  FormControl,
  LoadingButton,
  MoreButton,
  PageTitle,
  PageWrapper,
  Tooltip,
} from '@ingrid/luterkort-react';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { StepTabs } from '~src/components/StepTabs/StepTabs';
import { VerticalSplit } from '~src/components/VerticalSplit/VerticalSplit';
import { useTranslation } from '~src/i18n/use-translation';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';

import { ChatDialog } from './components/chat-dialog';
import { ConfiguratorProvider, useConfiguratorContext } from './components/configurator-context';
import { RuleEditor } from './components/rule-editor';
import { TestRunnerFinalOutput } from './components/test-runner-final-output';
import { TestRunnerInput } from './components/test-runner-input';
import { TestRunnerOutput } from './components/test-runner-output';

function InnerPage() {
  const [marks, setMarks] = useState<Monaco.editor.IMarker[]>([]);
  const [showTestRunner, setShowTestRunner] = useState(false);
  const [method] = useQueryState('method', parseAsString);

  const { isPublishing, tab, hasTestOutput, setTab } = useConfiguratorContext();
  const { t } = useTranslation();

  const tracking = useTracking();

  function handleMount(editor: Monaco.editor.IStandaloneCodeEditor) {
    if (method) {
      const model = editor.getModel()!;
      const match = model.findNextMatch(
        `"method": "${method}"`, // search query
        new Monaco.Position(1, 1), // start the searching at the specified position
        false, // indicate that we're not searching for a regex
        false, // indicate that it should not be case sensitive
        null, // indicate to not match entire words only
        true // the result should contain the matches
      );

      if (match) {
        editor.revealPositionInCenter({ lineNumber: match.range.startLineNumber, column: 1 });
      }
    }
  }

  function handleClickTestRunner() {
    tracking.capture(TrackingEventEnum.ConfiguratorClickTestMode);
    setShowTestRunner(true);
  }

  function handleTabChange(t: string) {
    tracking.capture(TrackingEventEnum.ConfiguratorClickTab, { tab: t });
    setTab(t);
  }

  return (
    <PageWrapper gap="lg" style={{ height: '100%' }}>
      <PageTitle.Wrapper>
        <PageTitle.Stack>
          <Breadcrumb aria-label={t('common.breadcrumbs')}>
            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger>
                <MoreButton style={{ display: 'inline', verticalAlign: 'top' }} />
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="start" sideOffset={6} style={{ minWidth: 140, width: 'auto' }}>
                <DropdownMenu.Item asChild>
                  <Link to="../..">{t('common.settings')}</Link>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>

            <Link to="..">{t('common.bookingRules')}</Link>

            <a aria-current="page">{t('common.edit')}</a>
          </Breadcrumb>
        </PageTitle.Stack>

        <Button
          href="https://www.notion.so/Custom-Booking-Methods-1f9a1680dfbf807f894dda3cce50bb6a"
          onClick={() => tracking.capture(TrackingEventEnum.ConfiguratorClickHelp)}
          variant="secondary"
          target="_blank"
          as="a"
        >
          <Question16 /> {t('common.help')}
        </Button>

        <ChatDialog
          trigger={
            <Button variant="secondary">
              <IngridAi16 />
              {t('common.ingrid-ai')}
            </Button>
          }
        />

        <Button variant="secondary" onClick={handleClickTestRunner}>
          {t('bookingRules.edit.testMode')}
        </Button>

        <ConditionalWrapper
          when={marks.length > 0}
          wrapper={c => (
            <Tooltip content={t('bookingRules.edit.rule.errorTooltip')}>
              <div>{c}</div>
            </Tooltip>
          )}
        >
          <FormControl.SubmitButton asChild>
            <LoadingButton disabled={marks.length > 0} loading={isPublishing}>
              {t('bookingRules.edit.publish')}
            </LoadingButton>
          </FormControl.SubmitButton>
        </ConditionalWrapper>
      </PageTitle.Wrapper>

      <VerticalSplit.Root
        style={{ maxHeight: 'calc(100vh - 152px)' }}
        bottomVisible={showTestRunner}
        initialBottomHeight={380}
      >
        <VerticalSplit.TopPane>
          <RuleEditor onValidate={marks => setMarks(marks)} onMount={handleMount} />
        </VerticalSplit.TopPane>

        <VerticalSplit.Handle />

        <VerticalSplit.BottomPane>
          <StepTabs.Root value={tab} onValueChange={handleTabChange} style={{ height: '100%' }}>
            <StepTabs.List>
              <StepTabs.Trigger value="input">{t('bookingRules.edit.tab.prepare-run')}</StepTabs.Trigger>
              <StepTabs.Trigger value="output" disabled={!hasTestOutput}>
                {t('bookingRules.edit.tab.step-output')}
              </StepTabs.Trigger>
              <StepTabs.Trigger value="final-output" disabled={!hasTestOutput}>
                {t('bookingRules.edit.tab.final-output')}
              </StepTabs.Trigger>
              <StepTabs.Close onClick={() => setShowTestRunner(false)} />
            </StepTabs.List>

            <StepTabs.Content value="input" tabIndex={-1}>
              <TestRunnerInput />
            </StepTabs.Content>

            <StepTabs.Content value="output" tabIndex={-1}>
              <TestRunnerOutput />
            </StepTabs.Content>

            <StepTabs.Content value="final-output" tabIndex={-1}>
              <TestRunnerFinalOutput />
            </StepTabs.Content>
          </StepTabs.Root>
        </VerticalSplit.BottomPane>
      </VerticalSplit.Root>
    </PageWrapper>
  );
}

export function Page() {
  return (
    <ConfiguratorProvider>
      <InnerPage />
    </ConfiguratorProvider>
  );
}
